import { useEffect, useState } from "react";
import { BadgeCheck, ClipboardList, Copy, KeyRound, ShoppingBag, Store, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import CriticalPinDialog from "../CriticalPinDialog";

const TYPE_TR = {
    supplier: { label: "Tedarikçi", icon: Store, cls: "bg-blue-100 text-blue-800 border-blue-200" },
    store: { label: "Aktar Mağaza", icon: ShoppingBag, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

const STATUS_TR = {
    pending: { label: "Beklemede", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    docs_pending: { label: "Evrak Bekleniyor", cls: "bg-purple-100 text-purple-800 border-purple-200" },
    approved: { label: "Onaylandı", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    active: { label: "Aktif", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    passive: { label: "Pasif", cls: "bg-stone-100 text-stone-600 border-stone-200" },
    rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-700 border-red-200" },
};

export default function RegistrationsTab() {
    const [regs, setRegs] = useState(null);
    const [filter, setFilter] = useState("all");
    const [busy, setBusy] = useState(null);
    const [pinTarget, setPinTarget] = useState(null);
    const [creds, setCreds] = useState(null);

    const load = () => api.get("/registrations").then((r) => setRegs(r.data)).catch(() => setRegs([]));
    useEffect(() => { load(); }, []);

    const setStatus = async (reg, status) => {
        setBusy(reg.registration_id);
        try {
            const r = await api.post(`/registrations/${reg.registration_id}/status`, { status });
            toast.success(`${reg.company_name} → ${STATUS_TR[status].label}`);
            if (r.data.credentials) setCreds(r.data.credentials);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setBusy(null);
        }
    };

    const del = async (pin) => {
        try {
            await api.delete(`/admin/records/registrations/${pinTarget.registration_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Başvuru kalıcı olarak silindi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    const filtered = (regs || []).filter((r) => filter === "all" || r.type === filter);
    const pendingCount = (regs || []).filter((r) => r.status === "pending").length;

    return (
        <div className="space-y-4" data-testid="registrations-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-2">
                    {[["all", "Tümü"], ["supplier", "Tedarikçi"], ["store", "Aktar Mağaza"]].map(([k, l]) => (
                        <button key={k} onClick={() => setFilter(k)} className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${filter === k ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`} data-testid={`reg-filter-${k}`}>{l}</button>
                    ))}
                </div>
                {pendingCount > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800" data-testid="pending-registrations-count">
                        <ClipboardList className="mr-1.5 inline h-4 w-4" /> {pendingCount} başvuru beklemede
                    </div>
                )}
            </div>
            {!regs ? (
                <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="space-y-3" data-testid="registrations-list">
                    {filtered.map((r) => {
                        const T = TYPE_TR[r.type];
                        return (
                            <div key={r.registration_id} className="rounded-2xl border bg-card p-5 shadow-sm" data-testid={`registration-card-${r.registration_id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted"><T.icon className="h-5 w-5 text-terra-600" /></div>
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-display font-bold" data-testid={`reg-company-${r.registration_id}`}>{r.company_name}</p>
                                                <Badge variant="outline" className={`text-[10px] ${T.cls}`}>{T.label}</Badge>
                                                <Badge variant="outline" className={`text-[10px] ${STATUS_TR[r.status]?.cls}`} data-testid={`reg-status-${r.registration_id}`}>{STATUS_TR[r.status]?.label || r.status}</Badge>
                                            </div>
                                            <p className="mt-1 text-xs text-muted-foreground">{r.contact_name} • {r.email} • {r.phone} • {r.city} • {fmtDate(r.created_at)}</p>
                                            {r.category_names?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{r.category_names.map((c) => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}</div>}
                                            {r.details && <p className="mt-2 text-sm text-muted-foreground">{r.details}</p>}
                                            {r.note && <p className="mt-1 text-xs italic text-muted-foreground">"{r.note}"</p>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <select
                                            className="h-9 rounded-md border bg-background px-2 text-xs"
                                            value={r.status}
                                            disabled={busy === r.registration_id}
                                            onChange={(e) => setStatus(r, e.target.value)}
                                            data-testid={`reg-status-select-${r.registration_id}`}
                                        >
                                            {Object.entries(STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                        </select>
                                        {r.status === "approved" && <BadgeCheck className="h-4 w-4 text-emerald-600" />}
                                        <Button size="sm" variant="outline" className="text-destructive" disabled={busy === r.registration_id} onClick={() => setPinTarget(r)} data-testid={`reg-delete-${r.registration_id}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {!filtered.length && <p className="rounded-2xl border border-dashed py-16 text-center text-sm text-muted-foreground" data-testid="registrations-empty">Başvuru bulunmuyor.</p>}
                </div>
            )}
            <CriticalPinDialog open={!!pinTarget} onClose={() => setPinTarget(null)} onSubmit={del} actionLabel={pinTarget ? `Başvuru silme: ${pinTarget.company_name}` : ""} />

            <Dialog open={!!creds} onOpenChange={(o) => { if (!o) setCreds(null); }}>
                <DialogContent className="max-w-md" data-testid="reg-credentials-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-terra-600" /> Hesap Oluşturuldu</DialogTitle>
                    </DialogHeader>
                    {creds && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                <span className="font-semibold text-foreground">{creds.email}</span> için {creds.role === "supplier" ? "tedarikçi" : "mağaza"} hesabı açıldı. Geçici şifre yalnızca bir kez gösterilir — kopyalayıp kullanıcıya iletin. Kullanıcı giriş yaptığında kendi panelini görebilir.
                            </p>
                            <div className="space-y-2">
                                {[["E-posta", creds.email, "reg-cred-email"], ["Geçici Şifre", creds.password, "reg-cred-password"]].map(([label, value, tid]) => (
                                    <div key={tid} className="flex items-center gap-2 rounded-xl border bg-muted/60 p-3">
                                        <span className="w-24 shrink-0 text-xs font-semibold">{label}</span>
                                        <code className="flex-1 break-all font-mono text-xs font-bold" data-testid={tid}>{value}</code>
                                        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Kopyalandı"); }} data-testid={`${tid}-copy`}><Copy className="h-3.5 w-3.5" /></Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setCreds(null)} data-testid="reg-credentials-close">Kapat</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
